import * as React from 'react'
import { ReactElement, useMemo } from 'react'
import { useFormikContext } from 'formik'
import FieldRow from './FieldRow'
import FormikInputField from '../../../../components/Formik/FormikInputField'
import FormikComboboxField from '../../../../components/Formik/FormikComboboxField'
import FormikPostalCodeField from '../../../components/Form/FormikPostalCodeField'
import CountryCombobox, { CountryComboboxProps } from '../../../../components/Combobox/i18n/CountryCombobox'
import useViewport, { Viewport } from '../../../../hooks/useViewport'

type Props = {
    /** Field prefix: 'address' for the main address, 'poBoxAddress' for the postbus address. */
    prefix: 'address' | 'poBoxAddress'
}

/**
 * A party address (main or postbus), rebuilt in Fluent v9. Country becomes required as soon as any
 * other address field is filled — enforced in the party validation schema.
 */
export default function AddressSection(props: Props): ReactElement {
    const { values } = useFormikContext<any>()
    const { isViewport, viewport } = useViewport()
    const { prefix } = props

    // Below 550px (narrow and tiny panes) three address fields no longer fit on one line, so they
    // regroup into three rows of two (HAM-4261).
    const viewportNarrow = useMemo<boolean>(() => isViewport('<=', Viewport.OutlookNew), [viewport])

    const street = <FormikInputField<any> labelKey="party.edit.address.street" fieldName={`${prefix}Street`} />
    const number = <FormikInputField<any> labelKey="party.edit.address.number" fieldName={`${prefix}Number`} />
    const locality = <FormikInputField<any> labelKey="party.edit.address.locality" fieldName={`${prefix}Locality`} />
    const postalCode = (
        <FormikPostalCodeField labelKey="party.edit.address.postalcode" fieldName={`${prefix}PostalCode`} country={values[`${prefix}Country`]} />
    )
    const country = (
        <FormikComboboxField<any, CountryComboboxProps>
            labelKey="party.edit.address.country"
            fieldName={`${prefix}Country`}
            component={CountryCombobox}
        />
    )
    const municipality = <FormikInputField<any> labelKey="party.edit.address.municipality" fieldName={`${prefix}Municipality`} />

    if (viewportNarrow) {
        return (
            <>
                <FieldRow template="2fr 1fr">
                    {street}
                    {number}
                </FieldRow>
                <FieldRow template="1fr 2fr">
                    {postalCode}
                    {locality}
                </FieldRow>
                <FieldRow template="1fr 1fr">
                    {country}
                    {municipality}
                </FieldRow>
            </>
        )
    }

    return (
        <>
            <FieldRow template="2fr 1fr 2fr">
                {street}
                {number}
                {locality}
            </FieldRow>
            <FieldRow template="1fr 2fr 2fr">
                {postalCode}
                {country}
                {municipality}
            </FieldRow>
        </>
    )
}
